import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import type { DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import { format, subDays } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { usuarioProfileService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

const LIMITE = 100;

const PAPEL_ROTULO: Record<string, string> = {
  Criou: 'criou',
  Responsável: 'responsável',
  Convidado: 'convidado',
  Participa: 'participa',
};

/** Agenda dos eventos que a pessoa criou ou participa (aba Calendário da página do usuário).
 *  Busca só o intervalo visível — mesma regra de sobreposição do backend (`calendarioProvider`). */
export function CalendarioAgenda({
  userId, empresaId, dataInicial,
}: {
  userId: string;
  empresaId?: string;
  /** Início do período filtrado na página (YYYY-MM-DD) — a agenda abre nele. */
  dataInicial?: string;
}) {
  const navigate = useNavigate();
  const { user, setEmpresaFiltroId } = useAuth();
  const [eventos, setEventos] = useState<EventInput[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [truncado, setTruncado] = useState<{ exibidos: number; total: number } | null>(null);
  const requisicaoRef = useRef(0);

  /** Sem edição própria aqui — clicar num evento leva pra tela real do Calendário, já no
   *  dia certo e com o evento aberto (decisão da Samantha, 28/09/2026). Pra superadmin, o
   *  Calendário exige uma empresa selecionada no filtro do topo antes de mostrar qualquer coisa —
   *  ajusta esse filtro pra empresa do evento antes de navegar (achado 28/09/2026). */
  const handleEventClick = useCallback((arg: EventClickArg) => {
    const dia = format(arg.event.start ?? new Date(), 'yyyy-MM-dd');
    const eventoEmpresaId = arg.event.extendedProps.empresaId as string | undefined;
    if (user?.tipo === 'superadmin' && eventoEmpresaId) setEmpresaFiltroId(eventoEmpresaId);
    navigate(`/calendario?data=${dia}&evento=${arg.event.id}`);
  }, [navigate, user, setEmpresaFiltroId]);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const req = ++requisicaoRef.current;
    setCarregando(true);
    usuarioProfileService
      .getAtividade(userId, 'calendario', {
        page: 1,
        limit: LIMITE,
        empresaId,
        de: format(arg.start, 'yyyy-MM-dd'),
        // `end` do FullCalendar é exclusivo
        ate: format(subDays(arg.end, 1), 'yyyy-MM-dd'),
      })
      .then((res) => {
        if (req !== requisicaoRef.current) return;
        setEventos(res.data.map((r) => {
          const papel = PAPEL_ROTULO[r.papel as string];
          return {
            id: String(r.id),
            title: papel ? `${r.titulo} (${papel})` : String(r.titulo ?? ''),
            start: r.data_inicio as string,
            end: (r.data_fim as string | null) ?? undefined,
            allDay: Boolean(r.dia_inteiro),
            backgroundColor: (r.cor as string | null) ?? undefined,
            borderColor: (r.cor as string | null) ?? undefined,
            extendedProps: { empresaId: r.empresa_id as string | null },
          };
        }));
        setTruncado(res.total > res.data.length ? { exibidos: res.data.length, total: res.total } : null);
      })
      .finally(() => {
        if (req === requisicaoRef.current) setCarregando(false);
      });
  }, [userId, empresaId]);

  return (
    <div className="space-y-2">
      {truncado && (
        <p className="text-xs text-amber-700">
          Mostrando {truncado.exibidos} de {truncado.total} eventos neste intervalo. Use a semana ou a lista
          pra ver os demais, ou exporte o CSV.
        </p>
      )}
      <div className="relative">
        {carregando && (
          <div className="absolute right-2 top-2 z-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        <FullCalendar
          key={empresaId ?? 'todas'}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          initialDate={dataInicial}
          locale={ptBrLocale}
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,listMonth' }}
          buttonText={{ today: 'Hoje', month: 'Mês', week: 'Semana', list: 'Lista' }}
          height="auto"
          dayMaxEvents
          events={eventos}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
        />
      </div>
    </div>
  );
}
